/* global ymaps */
import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';

// Инициализация GridLayer.YandexMutant (выполняется один раз)
let yandexMutantInitialized = false;

const initializeYandexMutant = () => {
    if (yandexMutantInitialized || !L.GridLayer) return;
    
    // Based on https://gitlab.com/IvanSanchez/Leaflet.GridLayer.GoogleMutant
    // and on https://github.com/shramov/leaflet-plugins
    // GridLayer like https://avinmathew.com/leaflet-and-google-maps/ , but using MutationObserver instead of jQuery
    
    L.GridLayer.YandexMutant = L.GridLayer.extend({
        includes: L.Mixin.Events,

        options: {
            minZoom: 0,
            maxZoom: 18,
            attribution: '',	
            opacity: 1,
            traffic: false,
            noWrap: false,
            type: 'yandex#map'
        },

        possibleShortMapTypes: {
            schemaMap: 'map',
            satelliteMap: 'satellite',
            hybridMap: 'hybrid',
            publicMap: 'publicMap',
            publicMapInHybridView: 'publicMapHybrid'
        },
        
        _getPossibleMapType: function (mapType) {
            var result = 'yandex#map';
            if (typeof mapType !== 'string') {
                return result;
            }
            for (var key in this.possibleShortMapTypes) {
                if (mapType === this.possibleShortMapTypes[key]) {
                    result = 'yandex#' + mapType;
                    break;
                }
                if (mapType === ('yandex#' + this.possibleShortMapTypes[key])) {
                    result = mapType;
                }
            }
            return result;
        },
        
        initialize: function (options) {
            if (options && options.type) {
                options.type = this._getPossibleMapType(options.type);
            }
            L.GridLayer.prototype.initialize.call(this, options);

            this._ready = !!window.ymaps && !!window.ymaps.Map;
            
            this._YAPIPromise = this._ready ? Promise.resolve(window.ymaps) : new Promise(function (resolve, reject) {
                var checkCounter = 0;
                var intervalId = null;
                intervalId = setInterval(function () {
                    if (checkCounter >= 10) {
                        clearInterval(intervalId);
                        return reject(new Error('window.ymaps not found after 10 attempts'));
                    }
                    if (!!window.ymaps) {
                        clearInterval(intervalId);
                        if (ymaps.Map === undefined) {
                            return ymaps.load(['package.map'], resolve, ymaps);
                        }
                        else {
                            return resolve(window.ymaps);
                        }
                    }
                    checkCounter++;
                }, 500);
            });

            this._tileCallbacks = {};
            this._freshTiles = {};

            this._imagesPerTile = 1;
            this.createTile = this._createSingleTile;
        },

        onAdd: function (map) {
            L.GridLayer.prototype.onAdd.call(this, map);
            this._initMutantContainer();

            this._YAPIPromise.then(function () {
                this._ready = true;
                this._map = map;
                
                this._initMutant();
                
                map.on('viewreset', this._reset, this);
                map.on('move', this._update, this);
                map.on('zoomend', this._handleZoomAnim, this);
                map.on('resize', this._resize, this);
                
                if (map._controlCorners && map._controlCorners.bottomright) {
                    map._controlCorners.bottomright.style.marginBottom = '4em';
                }

                this._reset();
                this._update();
            }.bind(this)).catch(function (error) {
                console.error('Ошибка загрузки Yandex Maps API:', error);
                console.warn('Убедитесь, что скрипт Yandex Maps API подключен в index.html:');
                console.warn('<script src="https://api-maps.yandex.ru/2.1/?lang=ru_RU" type="text/javascript"></script>');
            }.bind(this));
        },

        onRemove: function (map) {
            L.GridLayer.prototype.onRemove.call(this, map);
            if (this._mutantContainer && map._container && map._container.contains(this._mutantContainer)) {
                map._container.removeChild(this._mutantContainer);
            }
            this._mutantContainer = undefined;

            // Отключаем observer для копирайта
            if (this._copyrightObserver) {
                this._copyrightObserver.disconnect();
                this._copyrightObserver = null;
            }

            map.off('viewreset', this._reset, this);
            map.off('move', this._update, this);
            map.off('zoomend', this._handleZoomAnim, this);
            map.off('resize', this._resize, this);

            if (map._controlCorners && map._controlCorners.bottomright) {
                map._controlCorners.bottomright.style.marginBottom = '0em';
            }
        },

        getAttribution: function () {
            return this.options.attribution;
        },

        setOpacity: function (opacity) {
            this.options.opacity = opacity;
            if (this._mutantContainer && opacity < 1) {
                L.DomUtil.setOpacity(this._mutantContainer, opacity);
            }
        },

        setElementSize: function (e, size) {
            e.style.width = size.x + 'px';
            e.style.height = size.y + 'px';
        },

        _initMutantContainer: function () {
            if (!this._mutantContainer) {
                this._mutantContainer = L.DomUtil.create('div', 'leaflet-yandex-mutant leaflet-top leaflet-left');
                this._mutantContainer.id = '_MutantContainer_' + L.Util.stamp(this._mutantContainer);
                this._mutantContainer.style.zIndex = 'auto';
                this._mutantContainer.style.pointerEvents = 'none';

                this._map.getContainer().appendChild(this._mutantContainer);
            }

            this.setOpacity(this.options.opacity);
            this.setElementSize(this._mutantContainer, this._map.getSize());

            this._attachObserver(this._mutantContainer);
        },

        _initMutant: function () {
            if (!this._ready || !this._mutantContainer) return;
            this._mutantCenter = [0, 0];

            if (this.options.traffic) {
                if (ymaps.control === undefined ||
                        ymaps.control.TrafficControl === undefined) {
                    return ymaps.load(['package.traffic', 'package.controls'],
                        this._initMutant, this);
                }
            }
            
            var map = new ymaps.Map(this._mutantContainer, {
                center: this._mutantCenter,
                zoom: 0,
                type: this.options.type,
                behaviors: [],
                controls: []
            }, {
                autoFitToViewport: 'none',
                exitFullscreenByEsc: false,
                yandexMapDisablePoiInteractivity: true
            });

            if (this.options.traffic) {
                map.controls.add(new ymaps.control.TrafficControl({shown: true}));
            }

            if (this.options.type === 'yandex#null') {
                this.options.type = new ymaps.MapType('null', []);
                map.container.getElement().style.background = 'transparent';
            }
            map.setType(this.options.type);
            
            this._mutant = map;

            // Скрытие копирайта Яндекс.Карт
            this._hideCopyright();

            this.fire('spawned', {mapObject: map});
        },

        _hideCopyright: function () {
            if (!this._mutantContainer) return;
            
            // Функция для скрытия элементов копирайта
            var hideCopyrightElements = function() {
                var container = this._mutantContainer;
                if (!container) return;
                
                // Ищем все элементы с классами, содержащими "copyright"
                var copyrightElements = container.querySelectorAll('[class*="copyright"], [class*="Copyright"], [id*="copyright"], [id*="Copyright"]');
                copyrightElements.forEach(function(el) {
                    el.style.display = 'none';
                    el.style.visibility = 'hidden';
                    el.style.opacity = '0';
                    el.style.height = '0';
                    el.style.width = '0';
                    el.style.overflow = 'hidden';
                });

                // Ищем элементы с классами ymaps, которые могут быть копирайтом
                var ymapsElements = container.querySelectorAll('[class*="ymaps"]');
                ymapsElements.forEach(function(el) {
                    var className = el.className || '';
                    var style = el.getAttribute('style') || '';
                    // Если элемент внизу карты и содержит текст копирайта
                    if ((style.indexOf('bottom') !== -1 || className.indexOf('bottom') !== -1) && 
                        (className.indexOf('copyright') !== -1 || el.textContent.indexOf('©') !== -1 || el.textContent.indexOf('Яндекс') !== -1)) {
                        el.style.display = 'none';
                        el.style.visibility = 'hidden';
                        el.style.opacity = '0';
                    }
                });
            }.bind(this);

            // Вызываем сразу
            hideCopyrightElements();

            // Вызываем с задержкой, так как элементы могут появиться позже
            setTimeout(hideCopyrightElements, 500);
            setTimeout(hideCopyrightElements, 1000);
            setTimeout(hideCopyrightElements, 2000);

            // Используем MutationObserver для отслеживания новых элементов
            if (this._copyrightObserver) {
                this._copyrightObserver.disconnect();
            }
            this._copyrightObserver = new MutationObserver(function(mutations) {
                hideCopyrightElements();
            });
            this._copyrightObserver.observe(this._mutantContainer, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['class', 'style']
            });
        },

        _attachObserver: function _attachObserver (node) {
            var observer = new MutationObserver(this._onMutations.bind(this));
            observer.observe(node, { childList: true, subtree: true });
        },

        _onMutations: function _onMutations (mutations) {
            for (var i = 0; i < mutations.length; ++i) {
                var mutation = mutations[i];
                for (var j = 0; j < mutation.addedNodes.length; ++j) {
                    var node = mutation.addedNodes[j];

                    if (node instanceof HTMLImageElement) {
                        this._onMutatedImage(node);
                    } else if (node instanceof HTMLElement) {
                        Array.prototype.forEach.call(node.querySelectorAll('img'), this._onMutatedImage.bind(this));
                    }
                }
            }
        },

        _roadRegexp: /!1i(\d+)!2i(\d+)!3i(\d+)!/,
        _satRegexp: /x=(\d+)&y=(\d+)&z=(\d+)/,
        _staticRegExp: /StaticMapService\.GetMapImage/,

        _onMutatedImage: function _onMutatedImage (imgNode) {
            var coords;
            var match = imgNode.src.match(this._roadRegexp);
            var sublayer, parent;

            if (match) {
                coords = {
                    z: match[1],
                    x: match[2],
                    y: match[3]
                };
                if (this._imagesPerTile > 1) { imgNode.style.zIndex = 1; }
                sublayer = 1;
            } else {
                match = imgNode.src.match(this._satRegexp);
                if (match) {
                    coords = {
                        x: match[1],
                        y: match[2],
                        z: match[3]
                    };
                }
                sublayer = 0;
            }

            if (coords) {
                var key = this._tileCoordsToKey(coords);
                if (this._imagesPerTile > 1) { key += '/' + sublayer; }
                if (key in this._tileCallbacks && this._tileCallbacks[key]) {
                    this._tileCallbacks[key].pop()(imgNode);
                    if (!this._tileCallbacks[key].length) { delete this._tileCallbacks[key]; }
                } else {
                    parent = imgNode.parentNode;
                    if (parent) {
                        parent.removeChild(imgNode);
                        parent.removeChild = L.Util.falseFn;
                    }
                    if (key in this._freshTiles) {
                        this._freshTiles[key].push(imgNode);
                    } else {
                        this._freshTiles[key] = [imgNode];
                    }
                }
            } else if (imgNode.src.match(this._staticRegExp)) {
                parent = imgNode.parentNode;
                if (parent) {
                    imgNode.parentNode.replaceChild(L.DomUtil.create('img'), imgNode);
                }
            }
        },

        _createSingleTile: function createTile (coords, done) {
            var key = this._tileCoordsToKey(coords);

            if (key in this._freshTiles) {
                var tile = this._freshTiles[key].pop();
                if (!this._freshTiles[key].length) { delete this._freshTiles[key]; }
                L.Util.requestAnimFrame(done);
                return tile;
            } else {
                var tileContainer = L.DomUtil.create('div');
                this._tileCallbacks[key] = this._tileCallbacks[key] || [];
                this._tileCallbacks[key].push( (function (c) {
                    return function (imgNode) {
                        var parent = imgNode.parentNode;
                        if (parent) {
                            parent.removeChild(imgNode);
                            parent.removeChild = L.Util.falseFn;
                        }
                        c.appendChild(imgNode);
                        done();
                    }.bind(this);
                }.bind(this))(tileContainer) );

                return tileContainer;
            }
        },

        _checkZoomLevels: function () {
            if ((this._map.getZoom() !== undefined) && (this._mutant.getZoom() !== this._map.getZoom())) {
                this._map.setZoom(this._mutant.getZoom());
            }
        },

        _reset: function () {
            this._initContainer();
        },

        _update: function () {
            L.GridLayer.prototype._update.call(this);
            if (!this._mutant) return;

            var center = this._map.getCenter();
            var _center = [center.lat, center.lng];

            this._mutant.setCenter(_center);
            var zoom = this._map.getZoom();
            if (zoom !== undefined) {
                this._mutant.setZoom(Math.round(this._map.getZoom()));
            }
        },

        _resize: function () {
            var size = this._map.getSize();
            if (this._mutantContainer.style.width === size.x &&
                this._mutantContainer.style.height === size.y)
                return;
            this.setElementSize(this._mutantContainer, size);
            if (!this._mutant) return;
            this._mutant.container.fitToViewport();
        },

        _handleZoomAnim: function () {
            var center = this._map.getCenter();
            var _center = [center.lat, center.lng];

            this._mutant.setCenter(_center);
            this._mutant.setZoom(Math.round(this._map.getZoom()));
        },

        _removeTile: function (key) {
            if (this._imagesPerTile > 1) {
                for (var i=0; i<this._imagesPerTile; i++) {
                    var key2 = key + '/' + i;
                    if (key2 in this._freshTiles) { delete this._freshTiles[key2]; }
                }
            } else {
                if (key in this._freshTiles) {
                    delete this._freshTiles[key];
                }
            }

            return L.GridLayer.prototype._removeTile.call(this, key);
        }
    });

    L.gridLayer.yandexMutant = function (options) {
        return new L.GridLayer.YandexMutant(options);
    };

    yandexMutantInitialized = true;
};

/**
 * Компонент для отображения Yandex карт через GridLayer.YandexMutant
 * 
 * @param {string} type - Тип карты: 'yandex#map', 'yandex#satellite', 'yandex#hybrid'
 * @param {number} opacity - Прозрачность слоя (0-1)
 * @param {boolean} traffic - Показывать ли слой пробок
 * @param {number} minZoom - Минимальный уровень зума
 * @param {number} maxZoom - Максимальный уровень зума
 */
const YandexMutantLayer = ({ 
    type = 'yandex#map', 
    opacity = 1, 
    traffic = false,
    minZoom = 0,
    maxZoom = 18
}) => {
    const map = useMap();
    const layerRef = useRef(null);
    const optionsRef = useRef({ type, opacity, traffic, minZoom, maxZoom });

    // Инициализируем GridLayer.YandexMutant один раз
    useEffect(() => {
        initializeYandexMutant();
    }, []);

    // Обновляем ref при изменении пропсов
    useEffect(() => {
        optionsRef.current = { type, opacity, traffic, minZoom, maxZoom };
    }, [type, opacity, traffic, minZoom, maxZoom]);

    useEffect(() => {
        if (!map) return;

        // Проверяем, что GridLayer.YandexMutant доступен
        if (!L.GridLayer || !L.GridLayer.YandexMutant) {
            console.error('YandexMutant GridLayer не инициализирован');
            return;
        }

        const options = optionsRef.current;

        // Если слой уже существует и тип изменился, пересоздаем его
        if (layerRef.current) {
            const currentType = layerRef.current.options?.type;
            if (currentType !== type) {
                map.removeLayer(layerRef.current);
                layerRef.current = null;
            } else {
                // Обновляем опции существующего слоя
                if (layerRef.current.setOpacity && layerRef.current.options.opacity !== opacity) {
                    layerRef.current.setOpacity(opacity);
                }
                return;
            }
        }

        // Создаем новый слой
        try {
            const layer = L.gridLayer.yandexMutant({
                type: options.type,
                opacity: options.opacity,
                traffic: options.traffic,
                minZoom: options.minZoom,
                maxZoom: options.maxZoom
            });

            // Обрабатываем ошибку загрузки Yandex Maps API
            if (layer._YAPIPromise) {
                layer._YAPIPromise.catch((error) => {
                    console.error('Ошибка загрузки Yandex Maps API:', error);
                    console.warn('Убедитесь, что скрипт Yandex Maps API подключен в index.html');
                    if (layerRef.current && map.hasLayer(layerRef.current)) {
                        map.removeLayer(layerRef.current);
                        layerRef.current = null;
                    }
                });
            }

            // Добавляем слой на карту
            layer.addTo(map);
            layerRef.current = layer;
        } catch (error) {
            console.error('Ошибка при создании YandexMutant слоя:', error);
        }

        // Очистка при размонтировании
        return () => {
            if (layerRef.current && map.hasLayer(layerRef.current)) {
                map.removeLayer(layerRef.current);
                layerRef.current = null;
            }
        };
    }, [map, type]); // type в зависимостях для пересоздания при смене типа

    // Отдельный эффект для обновления opacity без пересоздания слоя
    useEffect(() => {
        if (layerRef.current && layerRef.current.setOpacity) {
            layerRef.current.setOpacity(opacity);
        }
    }, [opacity]);

    return null;
};

export default YandexMutantLayer;

